import { api } from './api.js';
import { esc, fmt$, signed$, fmtMonths, closeSheet, showToast, presentSheet} from './utils.js';

let subView = 'networth'; // 'networth' | 'budget' | 'trades'
let tradeJournalFilter = { period: 'all', status: 'all' }; // period: 'all' | 'month'; status: 'all' | 'open' | 'closed'

// Same helper as pulse.js's Voice card — kept as an independent copy rather
// than a shared import since these two files don't currently share a module
// for view-local formatting helpers. Wraps already-escaped numeric
// substrings in accent-colored markup; never touches raw user input.
function boldNumbers(escapedText) {
  return escapedText.replace(
    /(\$[\d,]+(?:\.\d+)?[KMkm]?|\b\d+(?:\.\d+)?%|\b\d+\s+months?\b)/g,
    '<strong style="color:var(--accent)">$1</strong>',
  );
}

// One stat tile in the Win/loss breakdown grid (UX_AUDIT W-22).
//
// The four tiles previously repeated the same 3 lines of markup and each
// rendered a bare "—" in its own semantic colour when it had nothing to show,
// so an account with no closed trades displayed four colour-coded em-dashes —
// green under Avg Win / Best trade, red under Avg Loss / Worst trade — that
// read as real figures. Two cards above, the Avg Thesis Accuracy card already
// handles this correctly: a tertiary-coloured placeholder plus a sentence
// naming what would fill it.
//
// Passing `amount: null` is how a caller says "this tile has no figure". The
// tile then drops the semantic colour rather than keeping it, because a green
// dash under AVG WIN asserts something the account has not earned. The
// "no closed trades at all" case is handled one level up, where the whole grid
// is replaced by a single sentence instead of four separate placeholders.
//
// Sign and colour are both derived from the amount rather than passed in, so a
// tile cannot state a sign or a colour that contradicts its own number. The
// four callers used to hardcode both, which held only while each tile's value
// carried the sign its label implies — and two of them don't: `bestTrade` is
// Math.max over closed trades (negative when every trade lost) and
// `worstTrade` is Math.min (positive when every trade won). See signed$ in
// utils.js for the two rendered symptoms.
const statTile = (label, amount) => {
  const color = amount === null || amount === 0 ? 'var(--text-tertiary)'
              : amount > 0                      ? 'var(--positive)'
              :                                   'var(--negative)';
  return `
  <div style="background:var(--bg-tertiary);border-radius:var(--radius-md);padding:var(--space-3)">
    <div style="font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">${label}</div>
    <div class="font-mono" style="font-size:1.1rem;font-weight:700;margin-top:4px;color:${color}">${signed$(amount)}</div>
  </div>`;
};

// Client-side mirror of calculations.ts's monthsToMillion() — used only for
// the Adjust Assumptions live-preview slider, where a network round trip per
// slider tick would feel laggy. Same compounding logic, same 1200-month cap.
function monthsToGoal(currentNetWorth, monthlySurplus, annualReturnPct, goalAmount) {
  const monthlyRate = annualReturnPct / 100 / 12;
  let balance = currentNetWorth;
  for (let m = 0; m < 1200; m++) {
    balance = balance * (1 + monthlyRate) + monthlySurplus;
    if (balance >= goalAmount) return m + 1;
  }
  return Infinity;
}

export async function renderProgress(el) {
  el.innerHTML = skeleton();

  try {
    const thisYear = new Date().getFullYear();
    const [progressData, cats, expenseCats, trades, income, expenses, rothList, paydaysThisYear, paydaysNextYear] = await Promise.all([
      api.dashboard.progress(),
      api.categories(),
      api.expenseCategories(),
      api.trades.list(),
      api.income.list(),
      api.expenses.list(),
      api.roth.list(),
      api.dashboard.paydays(thisYear),
      api.dashboard.paydays(thisYear + 1),
    ]);
    // Merge this-year + next-year so a query made in December still finds a
    // real "next payday" instead of running off the end of the list.
    const paydays = {
      paydays:       [...paydaysThisYear.paydays, ...paydaysNextYear.paydays],
      commissionDays: [...paydaysThisYear.commissionDays, ...paydaysNextYear.commissionDays],
    };
    renderProgressLayout(el, progressData, cats, expenseCats, trades, income, expenses, rothList, paydays);
  } catch (e) {
    el.innerHTML = `<p style="color:var(--negative)">Failed to load: ${esc(e.message)}</p>`;
  }
}

function renderProgressLayout(el, data, cats, expenseCats, trades, income, expenses, rothList, paydays) {
  const { now, future } = data;
  const { surplusThisMonth, monthIncome, monthExpenses, tradePnlThisMonth } = now;
  const { netWorth, latestSnapshot, projections, monthlySurplus, goalAmount, milestone, milestones, monthsToNextMilestone, voice, snapshotHistory, birthYear, accelerate } = future;

  // Upcoming flags — next payday / next commission check, both from the
  // server's own pay-period math (no client-side date logic to drift out of
  // sync). Insurance/rent-start flags from the roadmap spec are skipped —
  // no data model tracks those dates anywhere in this app.
  const todayForFlags = new Date().toISOString().split('T')[0];
  const nextPayday       = paydays.paydays.filter(d => d >= todayForFlags).sort()[0] ?? null;
  const nextCommissionDay = paydays.commissionDays.filter(d => d >= todayForFlags).sort()[0] ?? null;
  const daysUntil = (dateStr) => Math.round((new Date(dateStr + 'T12:00:00') - new Date(todayForFlags + 'T12:00:00')) / 86_400_000);

  const milestoneProgress = milestone.progress ?? 0;
  const nextTarget        = milestone.next?.amount ?? 1_000_000;
  const prevTarget        = milestone.current?.amount ?? 0;
  const amountToGo         = Math.max(0, nextTarget - netWorth);

  // Group expenses by category
  // Grouped by the money taxonomy (#63 / UX_AUDIT B-4), not the Work/School/Growth/
  // Fun time categories. Under the old grouping, rent, phone, wifi, gym, gas,
  // electric, subscriptions and insurance ALL landed in "Growth" — 57% of spending
  // in one bucket that answered no question anyone would ask of a budget.
  const expenseByCat = expenses.reduce((acc, e) => {
    const catId = e.expenseCategoryId ?? 'uncategorized';
    acc[catId] = (acc[catId] || 0) + e.amount;
    return acc;
  }, {});

  const totalExpenseAmt = expenses.reduce((sum, e) => sum + e.amount, 0);

  // Recurring vs. one-off split. The schema only has a binary `isRecurring`
  // flag (no separate "variable" concept exists anywhere in expense_entries
  // — recurringIntervalDays/recurringEndDate only ever apply when
  // isRecurring is true), so this is 2 real buckets, not the 3 the roadmap
  // sketches. A "variable" bucket would have nothing real behind it without
  // a genuinely new field — not fabricating one.
  const recurringExpenses = expenses.filter(e => e.isRecurring);
  const oneOffExpenses    = expenses.filter(e => !e.isRecurring);
  const recurringTotal    = recurringExpenses.reduce((s, e) => s + e.amount, 0);
  const oneOffTotal       = oneOffExpenses.reduce((s, e) => s + e.amount, 0);

  // Income breakdown by type (salary/commission/other) — YTD and this-month,
  // for the dedicated Income card. `income` is the full all-time list
  // (api.income.list() with no params), so both figures are computed here
  // client-side rather than needing a new endpoint.
  const nowForIncome  = new Date();
  const incomeYear    = nowForIncome.getFullYear();
  const incomeMonthStr = nowForIncome.toISOString().slice(0, 7);
  const netIncomeRows = income.filter(i => !i.isGross);
  const incomeByType = ['salary', 'commission', 'other'].map(type => {
    const rows = netIncomeRows.filter(i => i.type === type);
    return {
      type,
      ytd:   rows.filter(i => i.payDate.startsWith(String(incomeYear))).reduce((s, i) => s + i.amount, 0),
      month: rows.filter(i => i.payDate.startsWith(incomeMonthStr)).reduce((s, i) => s + i.amount, 0),
    };
  }).filter(t => t.ytd > 0 || t.month > 0);
  const incomeYtdTotal = incomeByType.reduce((s, t) => s + t.ytd, 0);

  // Group categories mapping — TIME categories, still used by tasks/blocks views.
  const catDetails = cats.reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});
  catDetails['uncategorized'] = { name: 'Uncategorized', color: 'var(--cat-uncategorized)' };

  // Money categories, looked up separately so a spending view can never resolve a
  // name out of the time taxonomy by accident (#63).
  const expenseCatDetails = (expenseCats ?? []).reduce((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});
  expenseCatDetails['uncategorized'] = { name: 'Uncategorized', color: 'var(--cat-uncategorized)' };

  // Combine income + expenses for transaction history
  const transactions = [
    ...income.map(i => ({ ...i, txType: 'income' })),
    ...expenses.map(e => ({ ...e, txType: 'expense' }))
  ].sort((a, b) => (b.payDate ?? b.date).localeCompare(a.payDate ?? a.date));

  // Compute Trades Metrics
  // netPnl already equals grossPnl when there are no fees — always safe to
  // show as "the" P&L rather than branching on whether fees were entered
  // (same convention as the Trade Detail Sheet in app.js). Fixes
  // KNOWN_INCONSISTENCIES #32 — these cards used to read grossPnl while the
  // Detail Sheet showed net, so the same trade displayed two different
  // profit figures depending where you looked.
  const closedTrades = trades.filter(t => t.status === 'closed' || t.status === 'expired_worthless');
  const tradePnl = (t) => t.pnl?.netPnl ?? t.pnl?.grossPnl ?? 0;
  const winningTrades = closedTrades.filter(t => tradePnl(t) > 0);
  const losingTrades  = closedTrades.filter(t => tradePnl(t) < 0);
  // Break-even closes (pnl exactly 0) belong to neither bucket. They still count
  // toward `closedTrades`, so omitting them made the breakdown fail to add up on
  // screen ("141W / 376L of 519 closed"). Surfaced, not silently folded into either.
  const scratchTrades = closedTrades.filter(t => tradePnl(t) === 0);
  const winRate = closedTrades.length > 0 ? Math.round((winningTrades.length / closedTrades.length) * 100) : 0;

  const tradesWithAccuracy = closedTrades.filter(t => t.thesisAccuracy != null);
  const avgAccuracy = tradesWithAccuracy.length > 0
    ? (tradesWithAccuracy.reduce((s, t) => s + t.thesisAccuracy, 0) / tradesWithAccuracy.length).toFixed(1)
    : '—';

  // All-time total — kept distinct from `tradePnlThisMonth` (from the
  // dashboard payload, genuinely month-scoped) since this card previously
  // labeled this same all-time sum "Monthly profit," which was just wrong
  // (found while rebuilding this section, not a new bug introduced here).
  const totalTradesPnl = closedTrades.reduce((s, t) => s + tradePnl(t), 0);
  const tradesYear = new Date().getFullYear();
  const ytdTrades = closedTrades.filter(t => (t.closedAt ?? t.openedAt).startsWith(String(tradesYear)));
  const ytdPnl = ytdTrades.reduce((s, t) => s + tradePnl(t), 0);

  const avgWin  = winningTrades.length > 0 ? winningTrades.reduce((s, t) => s + tradePnl(t), 0) / winningTrades.length : 0;
  const avgLoss = losingTrades.length  > 0 ? losingTrades.reduce((s, t) => s + tradePnl(t), 0)  / losingTrades.length  : 0;
  const bestTrade  = closedTrades.length > 0 ? Math.max(...closedTrades.map(tradePnl)) : 0;
  const worstTrade = closedTrades.length > 0 ? Math.min(...closedTrades.map(tradePnl)) : 0;

  // Monthly P&L for the bar chart — last 6 calendar months including the
  // current one, oldest first, grouped by closedAt's month.
  const monthlyPnl = (() => {
    const months = [];
    const cursor = new Date();
    cursor.setDate(1);
    for (let i = 5; i >= 0; i--) {
      const d = new Date(cursor);
      d.setMonth(d.getMonth() - i);
      months.push(d.toISOString().slice(0, 7));
    }
    return months.map(monthStr => ({
      month: monthStr,
      pnl: closedTrades
        .filter(t => (t.closedAt ?? t.openedAt).startsWith(monthStr))
        .reduce((s, t) => s + tradePnl(t), 0),
    }));
  })();

  const patternAnalysis = computePatternAnalysis(closedTrades);

  // ── Roth IRA — contributions can be logged via the FAB, but nothing in
  // Progress ever displayed them again afterward (KNOWN_INCONSISTENCIES).
  // rothList was already fetched above; this is the first thing that
  // actually uses it. birthYear comes from the dashboard/progress payload
  // (dashboard.ts), not a separate api.auth.session() call — see that
  // route's comment for why (a second call would've drawn from the same
  // rate-limited budget Settings already uses for the same purpose).
  const ROTH_LIMIT   = 7_000;
  const nowDate      = new Date();
  const currentYear  = nowDate.getFullYear();
  const rothThisYear = rothList.filter(r => r.year === currentYear);
  const rothContributed = rothThisYear.reduce((s, r) => s + r.amount, 0);
  const rothPct      = Math.min(100, (rothContributed / ROTH_LIMIT) * 100);
  const rothRemaining = Math.max(0, ROTH_LIMIT - rothContributed);
  const monthsLeftInYear = 12 - nowDate.getMonth(); // Dec (11) -> 1 month left, not 0
  const rothMonthlyTarget = rothRemaining > 0 ? rothRemaining / monthsLeftInYear : 0;

  let rothAt65 = null;
  if (birthYear) {
    const age = currentYear - birthYear;
    const yearsTo65 = Math.max(0, 65 - age);
    rothAt65 = ROTH_LIMIT * Math.pow(1.07, yearsTo65);
  }

  el.innerHTML = `
    <div style="max-width:720px;margin:0 auto">

      <!-- SUB-NAVIGATION -->
      <div style="display:flex;gap:8px;margin-bottom:var(--space-5);background:var(--bg-secondary);border-radius:var(--radius-md);padding:4px">
        <button class="progress-sub-btn" data-sub="networth"
          style="flex:1;padding:8px;border-radius:6px;font-weight:600;font-size:.875rem;transition:background .15s;
                 background:${subView==='networth'?'var(--bg-tertiary)':'transparent'};
                 color:${subView==='networth'?'var(--text-primary)':'var(--text-secondary)'}">Net Worth</button>
        <button class="progress-sub-btn" data-sub="budget"
          style="flex:1;padding:8px;border-radius:6px;font-weight:600;font-size:.875rem;transition:background .15s;
                 background:${subView==='budget'?'var(--bg-tertiary)':'transparent'};
                 color:${subView==='budget'?'var(--text-primary)':'var(--text-secondary)'}">Budget</button>
        <button class="progress-sub-btn" data-sub="trades"
          style="flex:1;padding:8px;border-radius:6px;font-weight:600;font-size:.875rem;transition:background .15s;
                 background:${subView==='trades'?'var(--bg-tertiary)':'transparent'};
                 color:${subView==='trades'?'var(--text-primary)':'var(--text-secondary)'}">Trades</button>
      </div>

      <!-- ── SECTION: NET WORTH ──────────────────────────────────────── -->
      <div id="sec-networth" style="display:${subView==='networth'?'block':'none'}">
        <div class="card">
          <div class="card-header">
            <span class="card-title">Net worth <span style="font-size:.75rem;font-weight:500;color:var(--text-tertiary)">(est.)</span></span>
            <span style="font-size:.75rem;color:var(--text-secondary)">Financial snapshot</span>
          </div>
          <div class="hero-number" style="color:var(--positive)">${fmt$(netWorth)}</div>
          ${latestSnapshot ? `<div class="hero-label">as of ${esc(latestSnapshot.date)}</div>` : ''}
        </div>

        <div class="card">
          <div class="card-header">
            <span class="card-title">Milestone goal: ${fmt$(nextTarget, 0)}</span>
            <span style="font-size:.75rem;color:var(--accent);font-weight:700">${Math.round(milestoneProgress * 100)}% Complete</span>
          </div>
          <div class="progress-bar" style="height:8px;margin-bottom:var(--space-3)">
            <div class="progress-fill" style="width:${Math.round(milestoneProgress*100)}%;background:var(--accent)"></div>
          </div>
          ${milestone.next ? `
            <div style="font-size:.95rem;font-weight:700">
              ${fmt$(amountToGo, 0)} to go <span style="color:var(--text-tertiary);font-weight:500">· ~${fmtMonths(monthsToNextMilestone)} at current pace</span>
            </div>
            <div style="margin-top:2px;font-size:.8rem;color:var(--text-secondary)">
              Next: <strong>${esc(milestone.next.label)}</strong> — ${esc(milestone.next.meaning)}
            </div>
            <!-- "at current pace" is driven by monthlySurplus — a TYPICAL month
                 estimated from the user's pay schedule — not by the actual
                 month-to-date surplus shown further down this same screen. That
                 figure was never displayed anywhere, so the two numbers read as a
                 contradiction with no way to reconcile them (UX_AUDIT F-6). -->
            <div style="margin-top:4px;font-size:.75rem;color:var(--text-tertiary)">
              Based on a typical month (estimated):
              <strong style="color:${monthlySurplus >= 0 ? 'var(--positive)' : 'var(--negative)'}">${monthlySurplus >= 0 ? '+' : ''}${fmt$(monthlySurplus)}</strong>/mo —
              not this month's running total below.
            </div>
            ${accelerate ? `<button id="milestone-faster-link" style="margin-top:6px;background:none;border:none;padding:0;font-size:.8rem;color:var(--accent);font-weight:600;cursor:pointer">What gets you there faster? ↓</button>` : ''}
          ` : `<div style="font-size:.9rem;color:var(--positive);font-weight:700">🎉 You've reached the final milestone.</div>`}
          ${milestone.current ? `<div style="margin-top:var(--space-2);font-size:.8rem;color:var(--text-tertiary)">Current: ${esc(milestone.current.label)} (${fmt$(prevTarget)})</div>` : ''}

          <button id="milestone-map-toggle" class="btn btn-ghost" style="width:100%;justify-content:center;margin-top:var(--space-3);padding:8px;font-size:.8rem">
            Show full milestone map ▾
          </button>
          <div id="milestone-map" style="display:none;margin-top:var(--space-3)">
            ${milestones.map(m => {
              const reached  = netWorth >= m.amount;
              const isCurrent = milestone.current?.amount === m.amount;
              const isNext    = milestone.next?.amount === m.amount;
              return `
              <div style="display:flex;align-items:center;gap:var(--space-3);padding:8px 0;border-bottom:1px solid var(--bg-tertiary)">
                <div style="width:22px;height:22px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;
                            background:${reached ? 'var(--positive)' : 'var(--bg-tertiary)'};
                            border:${isNext ? '2px solid var(--accent)' : 'none'};font-size:.7rem">
                  ${reached ? '✓' : ''}
                </div>
                <div style="flex:1">
                  <div style="font-size:.8rem;font-weight:${isCurrent || isNext ? '700' : '500'};color:${reached ? 'var(--text-primary)' : 'var(--text-secondary)'}">${esc(m.label)}</div>
                  <div style="font-size:.7rem;color:var(--text-tertiary)">${esc(m.meaning)}</div>
                </div>
                <div class="font-mono" style="font-size:.8rem;font-weight:600;color:${reached ? 'var(--positive)' : 'var(--text-tertiary)'}">${fmt$(m.amount, 0)}</div>
              </div>`;
            }).join('')}
          </div>

          <div style="margin-top:var(--space-5);display:grid;grid-template-columns:repeat(3,1fr);gap:var(--space-3);text-align:center">
            ${[
              { label: 'Conservative', key: 'conservative', rate: '5%' },
              { label: 'Moderate',     key: 'moderate',     rate: '7%' },
              { label: 'Optimistic',   key: 'optimistic',   rate: '10%' },
            ].map(s => { const isModerate = s.key === 'moderate'; return `
              <div style="background:var(--bg-tertiary);border-radius:var(--radius-md);padding:var(--space-3)${isModerate ? ';border:1px solid var(--accent)' : ''}">
                <div style="font-size:.65rem;color:${isModerate ? 'var(--accent)' : 'var(--text-tertiary)'};text-transform:uppercase;letter-spacing:.06em;font-weight:600">${s.label}</div>
                <div class="font-mono" style="font-size:1rem;font-weight:700;margin-top:4px;color:${isModerate ? 'var(--accent)' : 'var(--text-primary)'}">${fmtMonths(projections[s.key])}</div>
                <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:2px">${s.rate} return</div>
              </div>`; }).join('')}
          </div>
          <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:var(--space-2);text-align:center">Assumes 7% annual return (Moderate) unless noted otherwise.</div>

          <button id="adjust-assumptions-toggle" class="btn btn-ghost" style="width:100%;justify-content:center;margin-top:var(--space-3);padding:8px;font-size:.8rem">
            Adjust assumptions ▾
          </button>
          <div id="adjust-assumptions-panel" style="display:none;margin-top:var(--space-3);padding-top:var(--space-3);border-top:1px solid var(--bg-tertiary)">
            <div class="form-group" style="margin-bottom:var(--space-3)">
              <label class="form-label" style="display:flex;justify-content:space-between">
                <span>Annual return</span>
                <span id="adjust-rate-display" class="font-mono">7.0%</span>
              </label>
              <input type="range" id="adjust-rate" min="3" max="15" step="0.5" value="7" style="width:100%;accent-color:var(--accent)">
            </div>
            <div style="text-align:center">
              <div style="font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">At this rate, ${fmt$(goalAmount, 0)} is reached in</div>
              <div class="font-mono" id="adjust-result" style="font-size:1.3rem;font-weight:700;color:var(--accent);margin-top:4px">${fmtMonths(projections.moderate)}</div>
            </div>
            <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:var(--space-2);text-align:center">
              Live preview only — this doesn't change your saved Settings → Financial Goals return rate.
            </div>
          </div>
        </div>

        ${accelerate ? `
        <div class="card" id="accelerate-card">
          <div class="card-title" style="margin-bottom:var(--space-3)">Accelerate by 1 year</div>
          <div style="display:flex;flex-direction:column;gap:var(--space-3)">
            <div style="display:flex;justify-content:space-between;align-items:center">
              <span style="font-size:.85rem;color:var(--text-secondary)">💵 Save more/month</span>
              <span class="font-mono" style="font-weight:700">+${fmt$(accelerate.extraPerMonth)}</span>
            </div>
            ${accelerate.extraHoursPerPeriod != null ? `
            <div style="display:flex;justify-content:space-between;align-items:center">
              <span style="font-size:.85rem;color:var(--text-secondary)">⏰ Work more hrs/period</span>
              <span class="font-mono" style="font-weight:700">${accelerate.extraHoursPerPeriod}h</span>
            </div>` : ''}
            <div style="display:flex;justify-content:space-between;align-items:center">
              <span style="font-size:.85rem;color:var(--text-secondary)">📈 Generate from trading/mo</span>
              <span class="font-mono" style="font-weight:700">+${fmt$(accelerate.extraPerMonth)}</span>
            </div>
          </div>
          <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:var(--space-3)">At the moderate (7%) scenario, any one of these — not all three added together — gets you there 12 months sooner.</div>
        </div>` : ''}

        <div class="card card-bordered-accent">
          <div class="card-title" style="margin-bottom:var(--space-2);font-size:.9rem;text-transform:uppercase;letter-spacing:.05em;color:var(--accent)">Voice Summary</div>
          <p style="font-size:.95rem;line-height:1.6;color:var(--text-primary);margin:0;margin-bottom:var(--space-3);font-style:italic">"${boldNumbers(esc(voice))}"</p>
          <button class="btn btn-ghost" id="progress-voice-expand-btn" style="padding:4px 8px;font-size:.7rem;color:var(--accent);display:flex;align-items:center;gap:4px">
            <span>What changed</span>
            <span id="progress-voice-expand-icon">↓</span>
          </button>
          <div id="progress-voice-details" style="display:none;margin-top:var(--space-3);padding-top:var(--space-3);border-top:1px solid var(--bg-tertiary)">
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3);font-size:.8rem">
              <div>
                <div style="color:var(--text-tertiary)">Surplus this month (so far)</div>
                <div style="font-weight:700;color:${surplusThisMonth >= 0 ? 'var(--positive)' : 'var(--negative)'}">${fmt$(surplusThisMonth)}</div>
              </div>
              <div>
                <div style="color:var(--text-tertiary)">Trading P&L (Month)</div>
                <div style="font-weight:700;color:${tradePnlThisMonth >= 0 ? 'var(--positive)' : 'var(--negative)'}">${fmt$(tradePnlThisMonth)}</div>
              </div>
              <div>
                <div style="color:var(--text-tertiary)">Net Income (Month)</div>
                <div style="font-weight:700;color:var(--text-primary)">${fmt$(monthIncome)}</div>
              </div>
              <div>
                <div style="color:var(--text-tertiary)">Expenses (Month)</div>
                <div style="font-weight:700;color:var(--text-primary)">${fmt$(monthExpenses)}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <span class="card-title">Roth IRA · ${currentYear}</span>
            <span style="font-size:.75rem;color:var(--accent);font-weight:700">${Math.round(rothPct)}%</span>
          </div>
          <div class="hero-number" style="font-size:1.75rem">${fmt$(rothContributed)}<span style="font-size:1rem;color:var(--text-tertiary);font-weight:500"> / ${fmt$(ROTH_LIMIT)}</span></div>
          <div class="progress-bar"><div class="progress-fill" style="width:${rothPct}%"></div></div>
          ${rothRemaining > 0
            ? `<div style="margin-top:var(--space-3);font-size:.85rem;color:var(--text-secondary)">
                ${fmt$(rothRemaining)} left to hit the ${currentYear} limit — about
                <strong style="color:var(--text-primary)">${fmt$(rothMonthlyTarget)}/mo</strong>
                to max out by December.
              </div>`
            : `<div style="margin-top:var(--space-3);font-size:.85rem;color:var(--positive);font-weight:600">✓ ${currentYear} limit maxed out.</div>`
          }
          <div style="margin-top:var(--space-1);font-size:.75rem;color:var(--text-tertiary)">
            Deadline: April 15, ${currentYear + 1} (IRS tax-filing deadline for ${currentYear}
            contributions — December is just the pacing goal above).
          </div>
          ${rothAt65 != null
            ? `<div style="margin-top:var(--space-3);padding-top:var(--space-3);border-top:1px solid var(--bg-tertiary);font-size:.8rem;color:var(--text-secondary)">
                This year's ${fmt$(ROTH_LIMIT)} limit, invested today at 7%/yr, becomes
                <strong style="color:var(--positive)">${fmt$(rothAt65)}</strong> by age 65.
              </div>`
            : `<div style="margin-top:var(--space-3);padding-top:var(--space-3);border-top:1px solid var(--bg-tertiary);font-size:.75rem;color:var(--text-tertiary)">
                Add your birthday in Settings to see what this becomes by age 65.
              </div>`
          }
          <button class="btn btn-ghost" id="log-roth-btn-nw" style="margin-top:var(--space-4);font-size:.8rem;padding:6px 12px">+ Log contribution</button>
          ${rothList.length > 0
            ? `<div style="margin-top:var(--space-4);border-top:1px solid var(--bg-tertiary);padding-top:var(--space-3)">
                <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:var(--space-2)">Contribution history</div>
                ${rothList.slice(0, 8).map(r => `
                  <div style="display:flex;justify-content:space-between;align-items:center;gap:var(--space-2);padding:var(--space-2) 0;border-bottom:1px solid var(--bg-tertiary);font-size:.8rem">
                    <span style="color:var(--text-secondary);flex:1;min-width:0">${esc(r.date)}${r.notes ? ` · <span style="font-style:italic">${esc(r.notes)}</span>` : ''}</span>
                    <span class="font-mono" style="font-weight:600">${fmt$(r.amount)}</span>
                    <span style="white-space:nowrap">
                      <button class="roth-edit-btn" data-id="${esc(r.id)}" title="Edit" aria-label="Edit contribution" style="background:none;border:none;cursor:pointer;color:var(--text-tertiary);padding:2px 4px;font-size:.8rem">✎</button>
                      <button class="roth-del-btn" data-id="${esc(r.id)}" title="Delete" aria-label="Delete contribution" style="background:none;border:none;cursor:pointer;color:var(--text-tertiary);padding:2px 4px;font-size:.8rem">🗑</button>
                    </span>
                  </div>`).join('')}
              </div>`
            : ''
          }
        </div>

        <div class="card">
          <div class="card-header" style="margin-bottom:var(--space-3)">
            <span class="card-title">Snapshot history</span>
            <button class="btn btn-ghost" id="add-snapshot-btn-nw" style="font-size:.75rem;padding:4px 8px">+ Add Snapshot</button>
          </div>
          ${renderNetWorthChart(snapshotHistory)}
          ${snapshotHistory.length === 0
            ? `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No snapshot history logged yet.</p>`
            : `<div style="overflow-x:auto">
                <table class="data-table" style="font-size:.85rem">
                  <thead>
                    <tr style="text-align:left;border-bottom:1px solid var(--bg-tertiary);color:var(--text-tertiary)">
                      <th>Date</th>
                      <th style="text-align:right">Roth IRA</th>
                      <th style="text-align:right">Webull</th>
                      <th style="text-align:right">Checking/Cash</th>
                      <th style="text-align:right">Net Worth</th>
                      <th style="text-align:right;width:1%"></th>
                    </tr>
                  </thead>
                  <tbody>
                    ${snapshotHistory.slice().reverse().map(s => `
                      <tr style="border-bottom:1px solid var(--bg-tertiary)">
                        <td style="color:var(--text-secondary)">${esc(s.date)}</td>
                        <td style="text-align:right">${fmt$(s.rothIra)}</td>
                        <td style="text-align:right">${fmt$(s.webull)}</td>
                        <td style="text-align:right">${fmt$(s.checking + s.savings + s.cashOnHand)}</td>
                        <td style="text-align:right;font-weight:700;color:var(--text-primary)">${fmt$(s.netWorth)}</td>
                        <td style="text-align:right;white-space:nowrap">
                          <button class="snap-edit-btn" data-id="${esc(s.id)}" title="Edit" aria-label="Edit snapshot" style="background:none;border:none;cursor:pointer;color:var(--text-tertiary);padding:2px 4px;font-size:.8rem">✎</button>
                          <button class="snap-del-btn" data-id="${esc(s.id)}" title="Delete" aria-label="Delete snapshot" style="background:none;border:none;cursor:pointer;color:var(--text-tertiary);padding:2px 4px;font-size:.8rem">🗑</button>
                        </td>
                      </tr>`).join('')}
                  </tbody>
                </table>
              </div>`
          }
        </div>
      </div>

      <!-- ── SECTION: BUDGET ─────────────────────────────────────────── -->
      <div id="sec-budget" style="display:${subView==='budget'?'block':'none'}">
        ${(nextPayday || nextCommissionDay) ? `
        <div class="card" style="padding:var(--space-3) var(--space-4)">
          <div style="display:flex;gap:var(--space-4)">
            ${nextPayday ? `
            <div style="flex:1">
              <div style="font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Next payday</div>
              <div style="font-size:.85rem;font-weight:700;margin-top:2px">${esc(nextPayday)} <span style="color:var(--text-tertiary);font-weight:500">(${daysUntil(nextPayday) === 0 ? 'today' : `in ${daysUntil(nextPayday)}d`})</span></div>
            </div>` : ''}
            ${nextCommissionDay ? `
            <div style="flex:1">
              <div style="font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Next commission check</div>
              <div style="font-size:.85rem;font-weight:700;margin-top:2px">${esc(nextCommissionDay)} <span style="color:var(--text-tertiary);font-weight:500">(${daysUntil(nextCommissionDay) === 0 ? 'today' : `in ${daysUntil(nextCommissionDay)}d`})</span></div>
            </div>` : ''}
          </div>
        </div>` : ''}

        ${latestSnapshot ? `
        <div class="card" style="padding:var(--space-3) var(--space-4);display:flex;justify-content:space-between;align-items:center">
          <div>
            <div style="font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Credit card debt</div>
            <div class="font-mono" style="font-size:1.1rem;font-weight:700;margin-top:2px;color:${latestSnapshot.creditCardDebt > 0 ? 'var(--negative)' : 'var(--text-primary)'}">${fmt$(latestSnapshot.creditCardDebt ?? 0)}</div>
            <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:2px">as of ${esc(latestSnapshot.date)}</div>
          </div>
          <button class="btn btn-ghost" id="update-cc-balance-btn" style="font-size:.75rem;padding:8px 12px">Update balance</button>
        </div>` : ''}

        <div class="card card-bordered-${surplusThisMonth >= 0 ? 'positive' : 'negative'}">
          <div class="card-header">
            <span class="card-title">Surplus this month (so far)</span>
            <span style="font-size:.75rem;color:var(--text-secondary)">${esc(new Date().toLocaleString('en-US',{month:'long',year:'numeric'}))}</span>
          </div>
          <div class="hero-number" style="color:${surplusThisMonth >= 0 ? 'var(--positive)' : 'var(--negative)'}">
            ${surplusThisMonth >= 0 ? '+' : ''}${fmt$(surplusThisMonth)}
          </div>
          
          <div style="margin-top:var(--space-4)">
            <div style="display:flex;justify-content:space-between;font-size:.75rem;margin-bottom:4px;color:var(--text-secondary)">
              <span>Expense limit ratio</span>
              <span>${monthIncome > 0 ? Math.round((monthExpenses/monthIncome)*100) : 0}% of net income</span>
            </div>
            <div class="progress-bar" style="height:6px;background:var(--bg-tertiary);border-radius:99px">
              <div class="progress-fill" style="width:${Math.min(100, monthIncome > 0 ? Math.round((monthExpenses/monthIncome)*100) : 0)}%;height:100%;border-radius:99px;background:${monthExpenses > monthIncome ? 'var(--negative)' : 'var(--positive)'}"></div>
            </div>
          </div>

          <div style="margin-top:var(--space-4);display:grid;grid-template-columns:1fr 1fr;gap:var(--space-4);border-top:1px solid var(--bg-tertiary);padding-top:var(--space-3)">
            <div>
              <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Income Logged</div>
              <div class="font-mono" style="font-size:1.15rem;font-weight:700;color:var(--positive);margin-top:4px">${fmt$(monthIncome)}</div>
            </div>
            <div>
              <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Spent this month</div>
              <div class="font-mono" style="font-size:1.15rem;font-weight:700;color:var(--negative);margin-top:4px">${fmt$(monthExpenses)}</div>
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3);margin-bottom:var(--space-4)">
          <button class="btn btn-ghost" id="log-income-btn-b" style="background:var(--bg-secondary);justify-content:center;padding:12px;font-size:.875rem">+ Log Income</button>
          <button class="btn btn-ghost" id="log-expense-btn-b" style="background:var(--bg-secondary);justify-content:center;padding:12px;font-size:.875rem">+ Log Expense</button>
        </div>

        <!-- Income breakdown -->
        <div class="card">
          <div class="card-header">
            <span class="card-title">Income</span>
            <span style="font-size:.75rem;color:var(--text-secondary)">${incomeYear} YTD: ${fmt$(incomeYtdTotal)}</span>
          </div>
          ${incomeByType.length === 0
            ? `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No income logged yet.</p>`
            : `<div style="overflow-x:auto">
                <table class="data-table" style="font-size:.8rem">
                  <thead>
                    <tr style="text-align:left;color:var(--text-tertiary)">
                      <th style="padding:6px 0;font-weight:600">Type</th>
                      <th style="padding:6px 0;text-align:right;font-weight:600">This month</th>
                      <th style="padding:6px 0;text-align:right;font-weight:600">${incomeYear} YTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${incomeByType.map(t => `
                      <tr style="border-top:1px solid var(--bg-tertiary)">
                        <td style="text-transform:capitalize">${esc(t.type)}</td>
                        <td class="font-mono" style="text-align:right;color:var(--positive)">${fmt$(t.month)}</td>
                        <td class="font-mono" style="text-align:right;font-weight:700;color:var(--positive)">${fmt$(t.ytd)}</td>
                      </tr>`).join('')}
                  </tbody>
                </table>
              </div>`
          }
        </div>

        <!-- Expenses by Category -->
        <div class="card">
          <div class="card-header">
            <span class="card-title">Spending by category</span>
            <!-- This sums every expense ever logged, while "Spent this month" above is
                 month-scoped. Both used to read "Total" on the same screen, with no way
                 to tell which was which (UX_AUDIT F-5). -->
            <span style="font-size:.75rem;color:var(--text-secondary)">All time: ${fmt$(totalExpenseAmt)}</span>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3);margin-bottom:var(--space-4)">
            <div style="background:var(--bg-tertiary);border-radius:var(--radius-md);padding:var(--space-3)">
              <div style="font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Recurring</div>
              <div class="font-mono" style="font-size:1rem;font-weight:700;margin-top:4px">${fmt$(recurringTotal)}</div>
              <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:2px">${recurringExpenses.length} entries</div>
            </div>
            <div style="background:var(--bg-tertiary);border-radius:var(--radius-md);padding:var(--space-3)">
              <div style="font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">One-off</div>
              <div class="font-mono" style="font-size:1rem;font-weight:700;margin-top:4px">${fmt$(oneOffTotal)}</div>
              <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:2px">${oneOffExpenses.length} entries</div>
            </div>
          </div>
          ${Object.keys(expenseByCat).length === 0
            ? `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No expenses logged yet.</p>`
            : Object.entries(expenseByCat).map(([catId, amt]) => {
                const cat = expenseCatDetails[catId] || { name: 'Uncategorized', color: 'var(--cat-uncategorized)' };
                const pct = totalExpenseAmt > 0 ? Math.round((amt / totalExpenseAmt) * 100) : 0;
                return `
                  <div style="margin-bottom:var(--space-3)">
                    <div style="display:flex;justify-content:space-between;font-size:.8rem;margin-bottom:4px">
                      <span style="display:flex;align-items:center;gap:6px;font-weight:600">
                        <span style="width:8px;height:8px;border-radius:50%;background:${esc(cat.color)}"></span>
                        ${esc(cat.name)}
                      </span>
                      <span style="color:var(--text-secondary)">
                        ${fmt$(amt)} (${pct}%)
                      </span>
                    </div>
                    <div class="progress-bar" style="height:6px;background:var(--bg-tertiary);border-radius:99px">
                      <div class="progress-fill" style="width:${pct}%;height:100%;border-radius:99px;background:${esc(cat.color)}"></div>
                    </div>
                  </div>`;
              }).join('')
          }
        </div>

        <!-- Transaction list -->
        <div class="card">
          <div class="card-title" style="margin-bottom:var(--space-3)">Transaction History</div>
          ${transactions.length === 0
            ? `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No transactions recorded.</p>`
            : `<div style="display:flex;flex-direction:column;gap:var(--space-2)">
                ${transactions.map(tx => {
                  const isInc = tx.txType === 'income';
                  const date = tx.payDate ?? tx.date;
                  const amt = tx.amount;
                  const color = isInc ? 'var(--positive)' : 'var(--negative)';
                  const prefix = isInc ? '+' : '–';
                  const descrip = isInc ? `Direct paycheck (${esc(tx.type)})` : esc(tx.name);
                  const subtext = tx.notes ? esc(tx.notes) : '';
                  // Recurring expenses are projected forward, so this list can lead with a
                  // date that hasn't happened yet. Label it rather than letting it read as
                  // money already spent.
                  const isScheduled = date > todayForFlags;
                  const catVal = !isInc ? (expenseCatDetails[tx.expenseCategoryId ?? 'uncategorized']?.name ?? 'Uncategorized') : 'Net Salary';
                  const catCol = !isInc ? (expenseCatDetails[tx.expenseCategoryId ?? 'uncategorized']?.color ?? 'var(--cat-uncategorized)') : 'var(--positive)';
                  
                  return `
                    <div class="tx-item" style="display:flex;align-items:center;justify-content:space-between;padding:10px;background:var(--bg-tertiary);border-radius:var(--radius-md);transition:background .2s">
                      <div style="display:flex;align-items:center;gap:var(--space-3)">
                        <div style="font-size:.70rem;text-align:center;min-width:38px;color:var(--text-tertiary);line-height:1.2">
                          <div>${new Date(date + 'T00:00:00Z').toLocaleDateString('en-US',{month:'short'})}</div>
                          <div style="font-size:.85rem;font-weight:700;color:var(--text-secondary)">${new Date(date + 'T00:00:00Z').getUTCDate()}</div>
                        </div>
                        <div style="width:2px;height:24px;background:var(--bg-secondary)"></div>
                        <div>
                          <div style="font-weight:700;font-size:.85rem;color:var(--text-primary)">${descrip}${isScheduled ? ` <span class="badge badge-medium" style="font-size:.6rem;padding:1px 6px;vertical-align:middle">Scheduled</span>` : ''}</div>
                          <div style="font-size:.75rem;color:var(--text-tertiary);display:flex;align-items:center;gap:4px">
                            <span style="width:6px;height:6px;border-radius:50%;background:${catCol}"></span>
                            <span>${catVal}</span>
                            ${subtext ? `· <span style="font-style:italic">${subtext}</span>` : ''}
                          </div>
                        </div>
                      </div>
                      <div style="display:flex;align-items:center;gap:var(--space-3)">
                        <span class="font-mono" style="font-weight:700;color:${color}">${prefix}${fmt$(amt, 2)}</span>
                        <div style="display:flex;gap:4px">
                          <button class="tx-edit-btn" data-id="${tx.id}" data-txtype="${tx.txType}" style="background:transparent;border:none;cursor:pointer;color:var(--accent);font-size:.8rem;padding:4px">✏️</button>
                        </div>
                      </div>
                    </div>`;
                }).join('')}
              </div>`
          }
        </div>
      </div>

      <!-- ── SECTION: TRADES ─────────────────────────────────────────── -->
      <div id="sec-trades" style="display:${subView==='trades'?'block':'none'}">
        <div class="card">
          <div class="card-header">
            <span class="card-title">${tradesYear} P&L</span>
            <span style="font-size:.75rem;color:var(--text-secondary)">${ytdTrades.length} closed trades YTD</span>
          </div>
          <div class="hero-number" style="color:${ytdPnl >= 0 ? 'var(--positive)' : 'var(--negative)'}">
            ${ytdPnl >= 0 ? '+' : ''}${fmt$(ytdPnl)}
          </div>
          <div style="font-size:.75rem;color:var(--text-tertiary);margin-top:2px">All-time: <span style="font-weight:700;color:${totalTradesPnl >= 0 ? 'var(--positive)' : 'var(--negative)'}">${totalTradesPnl >= 0 ? '+' : ''}${fmt$(totalTradesPnl)}</span> · This month: <span style="font-weight:700;color:${tradePnlThisMonth >= 0 ? 'var(--positive)' : 'var(--negative)'}">${tradePnlThisMonth >= 0 ? '+' : ''}${fmt$(tradePnlThisMonth)}</span></div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3);margin-bottom:var(--space-4)">
          <div class="card" style="margin:0;padding:var(--space-4)">
            <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Trading win rate</div>
            <div class="hero-number" style="font-size:2rem;margin:8px 0 4px;color:var(--accent)">${winRate}%</div>
            <div style="font-size:.75rem;color:var(--text-secondary)">${winningTrades.length}W / ${losingTrades.length}L${scratchTrades.length ? ` / ${scratchTrades.length} break-even` : ''} of ${closedTrades.length} closed</div>
          </div>
          <div class="card" style="margin:0;padding:var(--space-4)">
            <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600">Avg Thesis Accuracy</div>
            ${tradesWithAccuracy.length > 0 ? `
              <div class="hero-number" style="font-size:2rem;margin:8px 0 4px">${avgAccuracy}<span style="font-size:1rem;color:var(--text-tertiary)">/10</span></div>
              <div style="font-size:.75rem;color:var(--text-secondary)">Across ${tradesWithAccuracy.length} reviewed trade${tradesWithAccuracy.length === 1 ? '' : 's'}</div>
            ` : `
              <div class="hero-number" style="font-size:2rem;margin:8px 0 4px;color:var(--text-tertiary)">—</div>
              <div style="font-size:.75rem;color:var(--text-tertiary)">Rate how well your thesis played out (1–10) when you review a closed trade.</div>
            `}
          </div>
        </div>

        <div class="card">
          <div class="card-title" style="margin-bottom:var(--space-3)">Win/loss breakdown</div>
          ${closedTrades.length === 0
            ? `<p class="color-tertiary text-sm" style="text-align:center;padding:12px 0">Close your first trade to see how your wins and losses compare.</p>`
            : `<div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-3);text-align:center">
                ${statTile('Avg Win',     winningTrades.length > 0 ? avgWin  : null)}
                ${statTile('Avg Loss',    losingTrades.length  > 0 ? avgLoss : null)}
                ${statTile('Best trade',  bestTrade)}
                ${statTile('Worst trade', worstTrade)}
              </div>`}
        </div>

        <div class="card">
          <div class="card-title" style="margin-bottom:var(--space-3)">Monthly P&L</div>
          ${closedTrades.length === 0
            ? `<p class="color-tertiary text-sm" style="text-align:center;padding:12px 0">No closed trades yet.</p>`
            : (() => {
                // UX_AUDIT F-9: every bar's value lived ONLY in a native `title`
                // attribute, which does not fire on touch at all — so on the phone
                // and tablet this product targets, the chart conveyed relative bar
                // heights and no numbers whatsoever. Values are now rendered as real
                // text, always visible, no hover required.
                //
                // The second defect, not in the audit: there was no zero baseline, so
                // a -$955 month and a +$96 month both grew UPWARD from the bottom and
                // were distinguished only by colour. Bars now hang below a zero line
                // when negative, which is what makes the sign readable at a glance.
                const maxAbs = Math.max(1, ...monthlyPnl.map(m => Math.abs(m.pnl)));
                return `<div class="pnl-chart">
                  ${monthlyPnl.map(m => {
                    const heightPct = Math.max(2, Math.round((Math.abs(m.pnl) / maxAbs) * 100));
                    const [y, mo] = m.month.split('-');
                    const label = new Date(`${y}-${mo}-01T12:00:00`).toLocaleDateString('en-US', { month: 'short' });
                    const pos   = m.pnl >= 0;
                    const color = pos ? 'var(--positive)' : 'var(--negative)';
                    const bar   = `<div class="pnl-bar" style="height:${heightPct}%;background:${color};border-radius:${pos ? '3px 3px 0 0' : '0 0 3px 3px'}"></div>`;
                    return `
                      <div class="pnl-col">
                        <div class="pnl-value" style="color:${color}">${pos ? '+' : ''}${fmt$(m.pnl)}</div>
                        <div class="pnl-half pnl-half-up">${pos ? bar : ''}</div>
                        <div class="pnl-zero"></div>
                        <div class="pnl-half pnl-half-down">${pos ? '' : bar}</div>
                        <div class="pnl-month">${label}</div>
                      </div>`;
                  }).join('')}
                </div>`;
              })()
          }
        </div>

        <div style="margin-bottom:var(--space-4)">${renderPatternAnalysis(patternAnalysis)}</div>

        <button class="btn btn-ghost" id="log-trade-btn-t" style="background:var(--bg-secondary);justify-content:center;padding:12px;font-size:.875rem;width:100%;margin-bottom:var(--space-3)">+ Log Trade</button>

        <div id="csv-dropzone" style="border:2px dashed var(--border-color);border-radius:var(--radius-md);padding:var(--space-5) var(--space-4);text-align:center;cursor:pointer;margin-bottom:var(--space-4);transition:border-color .15s,background .15s">
          <input type="file" id="webull-file-input" accept=".csv" style="display:none">
          <div style="font-size:1.5rem;margin-bottom:6px">📤</div>
          <div style="font-size:.85rem;font-weight:600">Drop a Webull CSV here</div>
          <div style="font-size:.75rem;color:var(--text-tertiary);margin-top:2px">or click to browse</div>
        </div>

        <div id="import-status" style="display:none;margin-bottom:var(--space-4)"></div>

        <div class="card">
          <div class="card-header" style="margin-bottom:var(--space-3)">
            <span class="card-title">Trade journal</span>
            <span style="font-size:.75rem;color:var(--text-secondary)" id="trade-journal-count"></span>
          </div>
          <div style="display:flex;gap:var(--space-2);margin-bottom:var(--space-3)">
            <select id="trade-journal-period" style="flex:1;font-size:.8rem">
              <option value="all"   ${tradeJournalFilter.period === 'all'   ? 'selected' : ''}>All time</option>
              <option value="month" ${tradeJournalFilter.period === 'month' ? 'selected' : ''}>This month</option>
            </select>
            <select id="trade-journal-status" style="flex:1;font-size:.8rem">
              <option value="all"    ${tradeJournalFilter.status === 'all'    ? 'selected' : ''}>All statuses</option>
              <option value="open"   ${tradeJournalFilter.status === 'open'   ? 'selected' : ''}>Open</option>
              <option value="closed" ${tradeJournalFilter.status === 'closed' ? 'selected' : ''}>Closed</option>
            </select>
          </div>
          <div id="trade-journal-table"></div>
        </div>
      </div>

    </div>`;

  // ── WIRE NAVIGATION TAB SWITCHES ──────────────────────────────────────────
  el.querySelectorAll('.progress-sub-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      subView = btn.dataset.sub;
      el.querySelectorAll('.progress-sub-btn').forEach(b => {
        b.style.background = 'transparent';
        b.style.color = 'var(--text-secondary)';
      });
      btn.style.background = 'var(--bg-tertiary)';
      btn.style.color = 'var(--text-primary)';
      el.querySelector('#sec-networth').style.display = subView === 'networth' ? 'block' : 'none';
      el.querySelector('#sec-budget').style.display   = subView === 'budget'   ? 'block' : 'none';
      el.querySelector('#sec-trades').style.display   = subView === 'trades'   ? 'block' : 'none';
    });
  });

  wireNetWorthChart(el, snapshotHistory);

  // Milestone map toggle + "what gets you there faster?" scroll link
  el.querySelector('#milestone-map-toggle')?.addEventListener('click', (e) => {
    const map = el.querySelector('#milestone-map');
    const isOpen = map.style.display !== 'none';
    map.style.display = isOpen ? 'none' : 'block';
    e.target.textContent = isOpen ? 'Show full milestone map ▾' : 'Hide milestone map ▴';
  });
  el.querySelector('#milestone-faster-link')?.addEventListener('click', () => {
    el.querySelector('#accelerate-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  // Voice Summary "What changed" toggle — same expand pattern as pulse.js's
  // Assistant Voice card.
  el.querySelector('#progress-voice-expand-btn')?.addEventListener('click', () => {
    const details = el.querySelector('#progress-voice-details');
    const icon = el.querySelector('#progress-voice-expand-icon');
    const isOpen = details.style.display !== 'none';
    details.style.display = isOpen ? 'none' : 'block';
    icon.textContent = isOpen ? '↓' : '↑';
  });

  // Adjust Assumptions — live client-side preview, no save, no network call
  // per tick (see monthsToGoal() at module scope). monthlySurplus/goalAmount
  // come straight from the dashboard payload the rest of this tab already
  // uses for its own real projections.
  el.querySelector('#adjust-assumptions-toggle')?.addEventListener('click', (e) => {
    const panel = el.querySelector('#adjust-assumptions-panel');
    const isOpen = panel.style.display !== 'none';
    panel.style.display = isOpen ? 'none' : 'block';
    e.target.textContent = isOpen ? 'Adjust assumptions ▾' : 'Hide assumptions ▴';
  });
  el.querySelector('#adjust-rate')?.addEventListener('input', (e) => {
    const rate = parseFloat(e.target.value);
    el.querySelector('#adjust-rate-display').textContent = `${rate.toFixed(1)}%`;
    el.querySelector('#adjust-result').textContent = fmtMonths(monthsToGoal(netWorth, monthlySurplus, rate, goalAmount));
  });

  // Net Worth Snapshot Save. Passes the currently-loaded netWorth + full
  // milestone list through so the sheet can detect a milestone *crossing*
  // (old < threshold <= new) the moment the new snapshot saves, not just
  // "are we currently past it" — that would fire the celebration on every
  // single snapshot after the first crossing, not just the one that did it.
  el.querySelector('#add-snapshot-btn-nw')?.addEventListener('click', () => {
    if (window.openSnapshotSheet) {
      window.openSnapshotSheet(el, netWorth, milestones, null, latestSnapshot);
    } else {
      document.getElementById('add-snapshot-btn')?.click();
    }
  });

  // Credit card "Update balance" — a new net worth snapshot is the only real
  // mechanism this app has for updating credit card debt (credit_card_months
  // is unused dead schema, confirmed by grep — no route ever reads or writes
  // it), so this opens the same snapshot form rather than inventing new
  // plumbing for a single field.
  el.querySelector('#update-cc-balance-btn')?.addEventListener('click', () => {
    if (window.openSnapshotSheet) window.openSnapshotSheet(el, netWorth, milestones, null, latestSnapshot);
  });

  // Edit / delete a net-worth snapshot — a fat-fingered value must be
  // correctable, not permanently baked into the headline chart (FABLE5 U-1).
  el.querySelectorAll('.snap-edit-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const snap = snapshotHistory.find(s => s.id === btn.dataset.id);
      if (snap && window.openSnapshotSheet) window.openSnapshotSheet(el, netWorth, milestones, snap);
    });
  });
  el.querySelectorAll('.snap-del-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const snap = snapshotHistory.find(s => s.id === btn.dataset.id);
      if (!snap) return;
      if (!confirm(`Delete the snapshot from ${snap.date} (net worth ${fmt$(snap.netWorth)})? This can't be undone from the app.`)) return;
      try {
        await api.networth.delete(snap.id);
        showToast('Snapshot deleted');
        await renderProgress(el);
      } catch (err) {
        showToast(err.message ?? 'Delete failed');
      }
    });
  });

  // Roth contribution
  el.querySelector('#log-roth-btn-nw')?.addEventListener('click', () => {
    if (window.openRothSheet) window.openRothSheet();
  });
  // Edit / delete a logged Roth contribution (FABLE5 U-1).
  el.querySelectorAll('.roth-edit-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const r = rothList.find(x => x.id === btn.dataset.id);
      if (r && window.openRothSheet) window.openRothSheet(r);
    });
  });
  el.querySelectorAll('.roth-del-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const r = rothList.find(x => x.id === btn.dataset.id);
      if (!r) return;
      if (!confirm(`Delete the ${fmt$(r.amount)} contribution from ${r.date}?`)) return;
      try {
        await api.roth.delete(r.id);
        showToast('Contribution deleted');
        await renderProgress(el);
      } catch (err) {
        showToast(err.message ?? 'Delete failed');
      }
    });
  });

  // Budget Sheets trigger
  el.querySelector('#log-income-btn-b')?.addEventListener('click', () => {
    if (window.openIncomeSheet) window.openIncomeSheet();
  });
  el.querySelector('#log-expense-btn-b')?.addEventListener('click', () => {
    if (window.openExpenseSheet) window.openExpenseSheet();
  });

  // Edit in Budget transaction list
  el.querySelectorAll('.tx-edit-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const txId = btn.dataset.id;
      const txType = btn.dataset.txtype;
      if (txType === 'income') {
        const entry = income.find(i => i.id === txId);
        if (entry && window.openIncomeSheet) window.openIncomeSheet(entry);
      } else {
        const entry = expenses.find(e => e.id === txId);
        if (entry && window.openExpenseSheet) window.openExpenseSheet(entry);
      }
    });
  });

  // Trades Sheets trigger
  el.querySelector('#log-trade-btn-t')?.addEventListener('click', () => {
    if (window.openTradeSheet) window.openTradeSheet();
  });

  // Trade Journal table — a literal Date/Symbol/P&L/Status table (roadmap
  // spec) instead of a card list, with All-time/This-month and status
  // filters. Re-rendered in place on filter change rather than a full
  // renderProgress() re-fetch, since the underlying `trades` data doesn't
  // change when the filter does.
  renderTradeJournalTable(el, trades);
  el.querySelector('#trade-journal-period')?.addEventListener('change', (e) => {
    tradeJournalFilter.period = e.target.value;
    renderTradeJournalTable(el, trades);
  });
  el.querySelector('#trade-journal-status')?.addEventListener('change', (e) => {
    tradeJournalFilter.status = e.target.value;
    renderTradeJournalTable(el, trades);
  });

  // ── WEBULL CSV UPLOAD ─────────────────────────────────────────────────────
  // Two-phase: a dry-run parse (nothing written to the DB) renders a preview
  // of the first 5 parsed trades + full imported/duplicate/error counts, and
  // only actually commits once the user taps "Confirm import".
  const fileInput = el.querySelector('#webull-file-input');
  const dropzone  = el.querySelector('#csv-dropzone');
  const statusEl  = el.querySelector('#import-status');

  const importRequest = async (file, dryRun) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dryRun', dryRun ? 'true' : 'false');
    const response = await fetch('/api/trades/import', { method: 'POST', body: formData });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error ?? 'Upload failed');
    return result;
  };

  const renderResultSummary = (result) => `
    <div class="card" style="margin:0">
      <div style="font-weight:700;color:var(--positive);margin-bottom:6px">Import completed!</div>
      <ul style="margin:0;padding-left:16px;font-size:.8rem;color:var(--text-secondary)">
        <li>Imported new trades: <strong>${result.imported.length}</strong></li>
        <li>Ignored duplicate symbols: <strong>${result.duplicates.length}</strong></li>
        <li>Errors: <strong>${result.errors.length}</strong></li>
      </ul>
      ${result.errors.length > 0 ? `<div style="font-size:.7rem;color:var(--negative);margin-top:6px">${result.errors.map(err => `<div>${esc(err)}</div>`).join('')}</div>` : ''}
    </div>`;

  const renderPreview = (file, result) => {
    const shown = result.preview.slice(0, 5);
    statusEl.style.display = 'block';
    statusEl.innerHTML = `
      <div class="card" style="margin:0">
        <div class="card-header" style="margin-bottom:var(--space-2)">
          <span class="card-title">Preview: ${esc(file.name)}</span>
          <span style="font-size:.75rem;color:var(--text-secondary)">${result.imported.length} new, ${result.duplicates.length} duplicate, ${result.errors.length} error${result.errors.length === 1 ? '' : 's'}</span>
        </div>
        ${shown.length ? `
        <div style="overflow-x:auto">
          <table class="data-table" style="font-size:.8rem">
            <thead><tr style="color:var(--text-tertiary);text-align:left">
              <th style="padding:4px 8px 4px 0">Ticker</th><th style="padding:4px 8px">Type</th>
              <th style="padding:4px 8px">Strike</th><th style="padding:4px 8px">Expiry</th><th style="padding:4px 0">Status</th>
            </tr></thead>
            <tbody>
              ${shown.map(p => `
                <tr style="border-top:1px solid var(--bg-tertiary)">
                  <td style="padding:4px 8px 4px 0;font-weight:600">${esc(p.ticker)}</td>
                  <td style="padding:4px 8px">${esc(p.optionType.toUpperCase())}</td>
                  <td style="padding:4px 8px" class="font-mono">$${p.strike}</td>
                  <td style="padding:4px 8px">${esc(p.expiry)}</td>
                  <td style="padding:4px 0">${esc(p.status)}</td>
                </tr>`).join('')}
            </tbody>
          </table>
        </div>
        ${result.preview.length > 5 ? `<div style="font-size:.7rem;color:var(--text-tertiary);margin-top:4px">+ ${result.preview.length - 5} more</div>` : ''}
        ` : `<p class="color-tertiary text-sm" style="padding:8px 0">Nothing new to import — ${result.duplicates.length ? 'all symbols already imported' : 'no parseable trades found'}.</p>`}
        ${result.errors.length ? `<div style="font-size:.7rem;color:var(--negative);margin-top:8px">${result.errors.map(err => `<div>${esc(err)}</div>`).join('')}</div>` : ''}
        <div style="display:flex;gap:var(--space-2);margin-top:var(--space-3)">
          <button class="btn btn-primary w-full" id="csv-confirm-btn" style="justify-content:center;padding:10px" ${shown.length ? '' : 'disabled'}>Confirm import</button>
          <button class="btn btn-ghost" id="csv-cancel-btn" style="padding:10px 16px">Cancel</button>
        </div>
        <p style="font-size:.7rem;color:var(--text-tertiary);margin-top:var(--space-2)">P&amp;L may differ from Webull by ~$0.10&ndash;0.15/contract.</p>
      </div>`;

    el.querySelector('#csv-cancel-btn').addEventListener('click', () => {
      statusEl.style.display = 'none';
      statusEl.innerHTML = '';
      fileInput.value = '';
    });
    el.querySelector('#csv-confirm-btn')?.addEventListener('click', async () => {
      const btn = el.querySelector('#csv-confirm-btn');
      btn.disabled = true;
      btn.textContent = 'Importing…';
      try {
        const real = await importRequest(file, false);
        // importRequest() uses its own fetch() (multipart upload), bypassing
        // api.js's request() — so it never triggers that file's own
        // clear-cache-on-write. Do it explicitly or the renderProgress()
        // below would serve the pre-import trade list back from cache.
        api.clearCache();
        statusEl.innerHTML = renderResultSummary(real);
          setTimeout(() => renderProgress(el), 2000);
      } catch (err) {
        statusEl.innerHTML = `<div class="card" style="margin:0"><span style="color:var(--negative);font-weight:700">Error:</span> ${esc(err.message)}</div>`;
      }
    });
  };

  const handleFile = async (file) => {
    if (!file) return;
    statusEl.style.display = 'block';
    statusEl.innerHTML = `<div class="card" style="margin:0">Parsing ${esc(file.name)}…</div>`;
    try {
      const result = await importRequest(file, true);
      renderPreview(file, result);
    } catch (err) {
      statusEl.innerHTML = `<div class="card" style="margin:0"><span style="color:var(--negative);font-weight:700">Error:</span> ${esc(err.message)}</div>`;
    }
  };

  dropzone?.addEventListener('click', () => fileInput.click());
  fileInput?.addEventListener('change', (e) => handleFile(e.target.files[0]));

  dropzone?.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.style.borderColor = 'var(--accent)';
    dropzone.style.background = 'rgba(var(--accent-rgb),.05)';
  });
  dropzone?.addEventListener('dragleave', () => {
    dropzone.style.borderColor = 'var(--border-color)';
    dropzone.style.background = 'transparent';
  });
  dropzone?.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.style.borderColor = 'var(--border-color)';
    dropzone.style.background = 'transparent';
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  });
}

// A distinct, more celebratory moment than the usual toast — reuses the
// sheet system rather than a full custom modal component, since that's
// already the app's mechanism for "something needs the user's attention
// right now." Roadmap: "milestone celebration on $25K net-worth crossing"
// (generalized here to any of the 6 milestones, not just the first one).
function showMilestoneCelebration(milestone) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  content.innerHTML = `
    <div style="text-align:center;padding:var(--space-4) 0">
      <div style="font-size:3rem;margin-bottom:var(--space-3)">🎉</div>
      <div class="sheet-title" style="margin-bottom:4px">Milestone reached!</div>
      <div class="hero-number" style="color:var(--positive);margin:var(--space-2) 0">${fmt$(milestone.amount, 0)}</div>
      <div style="font-size:1rem;font-weight:700;margin-bottom:4px">${esc(milestone.label)}</div>
      <div style="font-size:.85rem;color:var(--text-secondary);margin-bottom:var(--space-5)">${esc(milestone.meaning)}</div>
      <button class="btn btn-primary w-full" id="milestone-celebration-dismiss" style="justify-content:center;padding:12px">Nice!</button>
    </div>`;

  content.querySelector('#milestone-celebration-dismiss').addEventListener('click', closeSheet);
  presentSheet({ label: 'Milestone reached' });
}

function openSnapshotSheet(parentEl, previousNetWorth, milestoneList, editSnapshot = null, latestSnapshot = null) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  const today   = new Date().toISOString().split('T')[0];

  // When editing, prefill from the existing snapshot. The form folds
  // savings into the "Checking & Savings" field (create stores savings:0),
  // so prefill that field with checking+savings to preserve the total.
  const s = editSnapshot;

  // Carry-forward source for a NEW snapshot: the most recent existing one.
  //
  // A net-worth snapshot is a *statement of position*, not a diff — every
  // field it omits is stored as a real number, not "unchanged". The form only
  // exposes 7 of the record's 10 money fields, so before this, creating a
  // snapshot silently wrote 0 into `otherAssets` and `otherLiabilities`:
  // someone carrying $30k of student loans took their routine monthly
  // snapshot and the app recorded their net worth ~$30k HIGHER than reality,
  // then ran the milestone-crossing check against that fabricated number and
  // could fire a celebration off it (found by audit 2026-08-19; the edit path
  // already preserved these two, the create path did not).
  //
  // Carrying the previous snapshot forward matches how people actually track
  // net worth month to month — you restate what moved, you don't re-derive
  // your whole balance sheet — and it means an omitted field keeps its last
  // known value instead of being asserted as zero.
  const base = s ?? latestSnapshot ?? null;
  const val = (n) => (n ? esc(String(n)) : '');

  content.innerHTML = `
    <div class="sheet-title">${s ? 'Edit' : 'Net worth'} snapshot</div>
    <form id="snapshot-form">
      <div class="form-group">
        <label class="form-label">Date</label>
        <input type="date" id="snap-date" value="${esc(s ? s.date : today)}" required>
      </div>
      ${!s && base ? `
      <div class="field-helper" style="margin:-4px 0 var(--space-3)">
        Prefilled from your last snapshot (${esc(base.date)}). Change what moved — anything you
        leave alone carries over, including any other assets or debts this form doesn't show.
      </div>` : ''}
      <div class="form-row">
        <div class="form-group"><label class="form-label">Cash on hand</label><input type="number" id="snap-cash" placeholder="0" step="0.01" value="${val(base?.cashOnHand)}" style="font-family:var(--font-mono)"></div>
        <div class="form-group"><label class="form-label">Checking & Savings</label><input type="number" id="snap-checking" placeholder="0" step="0.01" value="${val(base ? (base.checking + base.savings) : 0)}" style="font-family:var(--font-mono)"></div>
      </div>
      <div class="form-row">
        <div class="form-group"><label class="form-label">Webull</label><input type="number" id="snap-webull" placeholder="0" step="0.01" value="${val(base?.webull)}" style="font-family:var(--font-mono)"></div>
        <div class="form-group"><label class="form-label">Venmo/Cash App</label><input type="number" id="snap-venmo" placeholder="0" step="0.01" value="${val(base?.venmoAndCashApp)}" style="font-family:var(--font-mono)"></div>
      </div>
      <div class="form-row">
        <div class="form-group"><label class="form-label">Physical assets</label><input type="number" id="snap-assets" placeholder="0" step="0.01" value="${val(base?.physicalAssets)}" style="font-family:var(--font-mono)"></div>
        <div class="form-group"><label class="form-label">Roth IRA</label><input type="number" id="snap-roth" placeholder="0" step="0.01" value="${val(base?.rothIra)}" style="font-family:var(--font-mono)"></div>
      </div>
      <div class="form-group">
        <label class="form-label">Credit card debt</label>
        <input type="number" id="snap-cc" placeholder="0" step="0.01" value="${val(base?.creditCardDebt)}" style="font-family:var(--font-mono)">
      </div>
      <div id="snap-err" class="auth-error"></div>
      <button type="submit" class="btn btn-primary w-full" style="justify-content:center;padding:12px;margin-top:8px">${s ? 'Save changes' : 'Save snapshot'}</button>
    </form>`;

  content.querySelector('#snapshot-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = content.querySelector('#snap-err');
    errEl.classList.remove('show');
    const get = (id) => parseFloat(document.getElementById(id)?.value) || 0;
    try {
      const body = {
        date:            content.querySelector('#snap-date').value,
        cashOnHand:      get('snap-cash'),
        checking:        get('snap-checking'),
        savings:         0,
        webull:          get('snap-webull'),
        venmoAndCashApp: get('snap-venmo'),
        physicalAssets:  get('snap-assets'),
        rothIra:         get('snap-roth'),
        creditCardDebt:  get('snap-cc'),
        // Preserve the fields this form doesn't expose. Previously this
        // carried them only on edit (`s`), so CREATE zeroed them — see the
        // `base` comment above. Now both paths inherit from `base`.
        otherAssets:      base ? base.otherAssets : 0,
        otherLiabilities: base ? base.otherLiabilities : 0,
      };
      // On edit, update in place; on create, insert. Only the create path can
      // cross a milestone for the first time, so celebration is create-only.
      const saved = s ? await api.networth.update(s.id, body) : await api.networth.create(body);
      closeSheet();
      // Milestone crossing detection: old < threshold <= new, not just
      // "are we currently past it" — the latter would re-fire the
      // celebration on every subsequent snapshot after the first crossing.
      // Picks the highest threshold crossed in case a big jump skips one
      // (e.g. a large bonus crossing two milestones in one snapshot).
      const crossed = !s ? (milestoneList ?? [])
        .filter(m => previousNetWorth != null && previousNetWorth < m.amount && saved.netWorth >= m.amount)
        .sort((a, b) => b.amount - a.amount)[0] : null;
      if (crossed) {
        showMilestoneCelebration(crossed);
      } else {
        showToast(s ? 'Snapshot updated' : 'Snapshot saved');
      }
      await renderProgress(parentEl);
    } catch (err) {
      errEl.textContent = err.message;
      errEl.classList.add('show');
    }
  });

  presentSheet();
}
// Expose snapshot globally
window.openSnapshotSheet = openSnapshotSheet;

// ── NET WORTH HISTORY CHART ──────────────────────────────────────────────
// Single time series (magnitude over time) -> line chart, no legend needed
// (dataviz skill: "none for one series"). Accent-colored line matching the
// app's existing hue — this isn't a categorical palette, so the CVD
// validator doesn't apply here. Empty until 2+ snapshots, per spec.

// Net worth chart's (x, y) SVG coordinates for each snapshot, oldest-to-
// newest, on a fixed W×H viewBox. Shared by renderNetWorthChart (draws the
// line/area) and wireNetWorthChart (hover crosshair) so both stay in sync by
// construction instead of independently re-deriving the same formula.
const NW_CHART_W = 600, NW_CHART_H = 140, NW_CHART_PAD = 8;

function computeNetWorthChartCoords(snapshotHistory) {
  const points = snapshotHistory.slice().reverse(); // oldest -> newest, left to right
  const values = points.map(p => p.netWorth);
  const minV = Math.min(...values), maxV = Math.max(...values);
  const range = maxV - minV || 1; // flat line guard
  const xAt = i => NW_CHART_PAD + (i / (points.length - 1)) * (NW_CHART_W - NW_CHART_PAD * 2);
  const yAt = v => NW_CHART_H - NW_CHART_PAD - ((v - minV) / range) * (NW_CHART_H - NW_CHART_PAD * 2);
  return { points, coords: points.map((p, i) => [xAt(i), yAt(p.netWorth)]) };
}

function renderNetWorthChart(snapshotHistory) {
  if (snapshotHistory.length < 2) {
    return `<p class="color-tertiary text-sm" style="text-align:center;padding:16px 0">
      ${snapshotHistory.length === 0 ? 'Take 2+ snapshots to see your net worth trend.' : 'Take one more snapshot to see a trend line.'}
    </p>`;
  }

  const { points, coords } = computeNetWorthChartCoords(snapshotHistory);
  const latest = points[points.length - 1];
  const previous = points[points.length - 2];
  const delta = latest.netWorth - previous.netWorth;
  const W = NW_CHART_W, H = NW_CHART_H;

  const linePath = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${coords[coords.length - 1][0].toFixed(1)},${H} L${coords[0][0].toFixed(1)},${H} Z`;

  return `
    <div style="display:flex;align-items:baseline;gap:var(--space-2);margin-bottom:var(--space-3)">
      <span class="hero-number" style="font-size:1.5rem">${fmt$(latest.netWorth)}</span>
      <span style="font-size:.8rem;font-weight:600;color:${delta >= 0 ? 'var(--positive)' : 'var(--negative)'}">${delta >= 0 ? '+' : ''}${fmt$(delta)} since ${esc(previous.date)}</span>
    </div>
    <div class="networth-chart" style="position:relative">
      <svg viewBox="0 0 ${W} ${H}" style="width:100%;height:120px;display:block" preserveAspectRatio="none">
        <defs>
          <linearGradient id="nw-area-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="var(--accent)" stop-opacity="0.18"/>
            <stop offset="100%" stop-color="var(--accent)" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <path d="${areaPath}" fill="url(#nw-area-fill)"/>
        <path d="${linePath}" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="${coords[coords.length - 1][0].toFixed(1)}" cy="${coords[coords.length - 1][1].toFixed(1)}" r="3.5" fill="var(--accent)"/>
        <line class="nw-crosshair" x1="0" y1="0" x2="0" y2="${H}" stroke="var(--text-tertiary)" stroke-width="1" stroke-dasharray="2,2" style="display:none"/>
        <circle class="nw-hover-dot" r="4" fill="var(--accent)" stroke="var(--bg-secondary)" stroke-width="2" style="display:none"/>
      </svg>
      <div class="nw-tooltip" style="position:absolute;top:0;pointer-events:none;display:none;background:var(--bg-tertiary);border:1px solid var(--bg-hover);border-radius:var(--radius-sm);padding:4px 8px;font-size:.7rem;white-space:nowrap;transform:translate(-50%,-100%);z-index:2"></div>
    </div>`;
}

// Called after the card's innerHTML (including the chart above) is in the
// DOM — wires the hover crosshair/tooltip. A no-op if there's no chart
// (fewer than 2 snapshots).
function wireNetWorthChart(el, snapshotHistory) {
  const svg = el.querySelector('.networth-chart svg');
  if (!svg || snapshotHistory.length < 2) return;

  const { points, coords } = computeNetWorthChartCoords(snapshotHistory);
  const W = NW_CHART_W, H = NW_CHART_H;

  const crosshair = svg.querySelector('.nw-crosshair');
  const hoverDot  = svg.querySelector('.nw-hover-dot');
  const tooltip   = el.querySelector('.nw-tooltip');

  function showAt(i) {
    const [x, y] = coords[i];
    const pctX = (x / W) * 100;
    crosshair.setAttribute('x1', x); crosshair.setAttribute('x2', x);
    crosshair.style.display = 'block';
    hoverDot.setAttribute('cx', x); hoverDot.setAttribute('cy', y);
    hoverDot.style.display = 'block';
    tooltip.style.display = 'block';
    tooltip.style.left = `${pctX}%`;
    tooltip.style.top = `${(y / H) * 120 - 8}px`;
    tooltip.innerHTML = `<strong>${fmt$(points[i].netWorth)}</strong><br><span style="color:var(--text-tertiary)">${esc(points[i].date)}</span>`;
  }
  function hide() {
    crosshair.style.display = 'none';
    hoverDot.style.display  = 'none';
    tooltip.style.display   = 'none';
  }

  function handleMove(clientX) {
    const rect = svg.getBoundingClientRect();
    const relX = ((clientX - rect.left) / rect.width) * W;
    let nearest = 0, nearestDist = Infinity;
    coords.forEach(([x], i) => {
      const d = Math.abs(x - relX);
      if (d < nearestDist) { nearestDist = d; nearest = i; }
    });
    showAt(nearest);
  }

  svg.addEventListener('mousemove', (e) => handleMove(e.clientX));
  svg.addEventListener('mouseleave', hide);
  svg.addEventListener('touchstart', (e) => handleMove(e.touches[0].clientX), { passive: true });
  svg.addEventListener('touchmove',  (e) => handleMove(e.touches[0].clientX), { passive: true });
  svg.addEventListener('touchend', hide);
}

// ── PATTERN ANALYSIS (Trades sub-view) ───────────────────────────────────
// Data-gated, cumulative levels — each level unlocks a deeper breakdown on
// top of the previous one's, computed client-side from the same `trades`
// array the Trading Summary cards already use (no new endpoint). Thresholds
// are this feature's own judgment call — the roadmap only specifies the
// shape ("data-gated, cumulative levels") and a success-criteria example
// ("Level 3 insights" at 299 trades from a bulk CSV import), not exact
// numbers, so these are picked to be reachable with realistic manual-entry
// trade counts, not just a large CSV import.
const PATTERN_LEVEL_THRESHOLDS = { 1: 1, 2: 5, 3: 15, 4: 30 };

function computePatternAnalysis(closedTrades) {
  const n = closedTrades.length;
  let level = 0;
  if (n >= PATTERN_LEVEL_THRESHOLDS[1]) level = 1;
  if (n >= PATTERN_LEVEL_THRESHOLDS[2]) level = 2;
  if (n >= PATTERN_LEVEL_THRESHOLDS[3]) level = 3;
  if (n >= PATTERN_LEVEL_THRESHOLDS[4]) level = 4;

  const result = { level, count: n };
  if (level < 1) return result;

  // Level 1: P&L by ticker
  const byTickerMap = {};
  for (const t of closedTrades) {
    const pnl = t.pnl?.netPnl ?? t.pnl?.grossPnl ?? 0;
    (byTickerMap[t.ticker] ??= { ticker: t.ticker, trades: 0, pnl: 0, wins: 0 });
    byTickerMap[t.ticker].trades++;
    byTickerMap[t.ticker].pnl += pnl;
    if (pnl > 0) byTickerMap[t.ticker].wins++;
  }
  result.byTicker = Object.values(byTickerMap).sort((a, b) => b.pnl - a.pnl);
  if (level < 2) return result;

  // Level 2: Calls vs puts (option trades only — a stock-only trader will
  // just see both sides at 0 trades, handled in the render)
  const summarize = (arr) => ({
    count: arr.length,
    wins:  arr.filter(t => (t.pnl?.netPnl ?? t.pnl?.grossPnl ?? 0) > 0).length,
    pnl:   arr.reduce((s, t) => s + (t.pnl?.netPnl ?? t.pnl?.grossPnl ?? 0), 0),
  });
  const optionTrades = closedTrades.filter(t => t.tradeType === 'option' && t.optionType);
  result.callsVsPuts = {
    calls: summarize(optionTrades.filter(t => t.optionType === 'call')),
    puts:  summarize(optionTrades.filter(t => t.optionType === 'put')),
  };
  if (level < 3) return result;

  // Level 3: win rate by closing day-of-week
  const DOW_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const byDow = DOW_NAMES.map(day => ({ day, trades: 0, wins: 0, pnl: 0 }));
  for (const t of closedTrades) {
    if (!t.closedAt) continue;
    const dow = new Date(t.closedAt + 'T12:00:00').getDay();
    byDow[dow].trades++;
    byDow[dow].pnl += t.pnl?.netPnl ?? t.pnl?.grossPnl ?? 0;
    if ((t.pnl?.netPnl ?? t.pnl?.grossPnl ?? 0) > 0) byDow[dow].wins++;
  }
  result.byDayOfWeek = byDow.filter(d => d.trades > 0);
  if (level < 4) return result;

  // Level 4: auto-generated insight callouts synthesizing the above —
  // each insight requires a minimum sample size so a single lucky/unlucky
  // trade doesn't get reported as a "pattern."
  const insights = [];
  const best  = result.byTicker[0];
  const worst = result.byTicker[result.byTicker.length - 1];
  if (best && best.pnl > 0) {
    insights.push(`${best.ticker} is your most profitable ticker (${fmt$(best.pnl)} across ${best.trades} trade${best.trades === 1 ? '' : 's'}).`);
  }
  if (worst && worst.pnl < 0 && worst !== best) {
    insights.push(`${worst.ticker} has cost you the most (${fmt$(worst.pnl)} across ${worst.trades} trade${worst.trades === 1 ? '' : 's'}).`);
  }
  const { calls, puts } = result.callsVsPuts;
  if (calls.count >= 3 && puts.count >= 3) {
    const callWinRate = Math.round((calls.wins / calls.count) * 100);
    const putWinRate  = Math.round((puts.wins / puts.count) * 100);
    if (Math.abs(callWinRate - putWinRate) >= 15) {
      const better = callWinRate > putWinRate ? 'calls' : 'puts';
      insights.push(`You win more often on ${better} (${better === 'calls' ? callWinRate : putWinRate}% vs ${better === 'calls' ? putWinRate : callWinRate}%).`);
    }
  }
  const bestDay = result.byDayOfWeek
    .filter(d => d.trades >= 3)
    .sort((a, b) => (b.wins / b.trades) - (a.wins / a.trades))[0];
  if (bestDay) {
    const pct = Math.round((bestDay.wins / bestDay.trades) * 100);
    insights.push(`Your best closing day is ${bestDay.day} (${pct}% win rate across ${bestDay.trades} trades).`);
  }
  result.insights = insights;

  return result;
}

function renderPatternAnalysis(analysis) {
  const { level, count } = analysis;

  if (level < 1) {
    return `
      <div class="card">
        <div class="card-header"><span class="card-title">Pattern analysis</span></div>
        <p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">Log your first closed trade to unlock pattern analysis.</p>
      </div>`;
  }

  const nextAt   = PATTERN_LEVEL_THRESHOLDS[level + 1];
  const maxLevel = Math.max(...Object.keys(PATTERN_LEVEL_THRESHOLDS).map(Number));
  // The level badge is meaningless without saying what a level is and how many exist.
  const progressNote = nextAt
    ? `<div style="font-size:.7rem;color:var(--text-tertiary);margin-top:4px">More closed trades unlock deeper analysis — ${nextAt - count} more to reach Level ${level + 1} of ${maxLevel}.</div>`
    : `<div style="font-size:.7rem;color:var(--text-tertiary);margin-top:4px">Level ${maxLevel} of ${maxLevel} — every pattern insight is unlocked.</div>`;

  return `
    <div class="card">
      <div class="card-header" style="margin-bottom:0">
        <span class="card-title">Pattern analysis</span>
        <span title="Analysis depth, unlocked by number of closed trades (Levels 1–${maxLevel} at ${Object.values(PATTERN_LEVEL_THRESHOLDS).join('/')} trades)" style="font-size:.7rem;color:var(--accent);background:rgba(var(--accent-rgb),.1);padding:2px 8px;border-radius:99px;font-weight:600">LEVEL ${level} / ${maxLevel}</span>
      </div>
      ${progressNote}

      ${analysis.insights?.length ? `
      <div style="margin:var(--space-3) 0;display:flex;flex-direction:column;gap:8px">
        ${analysis.insights.map(i => `
          <div style="display:flex;gap:8px;align-items:flex-start;padding:8px;background:var(--bg-tertiary);border-radius:var(--radius-sm);border-left:2px solid var(--accent)">
            <span>💡</span><span style="font-size:.8rem;color:var(--text-secondary)">${esc(i)}</span>
          </div>`).join('')}
      </div>` : ''}

      ${analysis.byTicker ? `
      <div style="margin-top:var(--space-3)">
        <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:8px">P&L by ticker</div>
        ${analysis.byTicker.slice(0, 6).map(t => `
          <div style="display:flex;justify-content:space-between;gap:8px;padding:4px 0;font-size:.8rem;border-bottom:1px solid var(--bg-tertiary)">
            <span style="font-weight:600">${esc(t.ticker)}</span>
            <span style="color:var(--text-tertiary);flex:1;text-align:right">${t.trades} trade${t.trades === 1 ? '' : 's'}</span>
            <span class="font-mono" style="font-weight:700;color:${t.pnl >= 0 ? 'var(--positive)' : 'var(--negative)'}">${t.pnl >= 0 ? '+' : ''}${fmt$(t.pnl)}</span>
          </div>`).join('')}
      </div>` : ''}

      ${analysis.callsVsPuts ? `
      <div style="margin-top:var(--space-3)">
        <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:8px">Calls vs puts</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-2)">
          ${['calls', 'puts'].map(k => {
            const d = analysis.callsVsPuts[k];
            const wr = d.count > 0 ? Math.round((d.wins / d.count) * 100) : 0;
            return `
              <div style="padding:8px;background:var(--bg-tertiary);border-radius:var(--radius-sm)">
                <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:capitalize">${k}</div>
                <div style="font-weight:700">${d.count > 0 ? `${wr}% win rate` : '—'}</div>
                <div style="font-size:.75rem;color:${d.pnl >= 0 ? 'var(--positive)' : 'var(--negative)'}">${d.count > 0 ? `${d.pnl >= 0 ? '+' : ''}${fmt$(d.pnl)}` : 'no trades'}</div>
              </div>`;
          }).join('')}
        </div>
      </div>` : ''}

      ${analysis.byDayOfWeek ? `
      <div style="margin-top:var(--space-3)">
        <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:8px">Win rate by closing day</div>
        ${analysis.byDayOfWeek.map(d => {
          const wr = Math.round((d.wins / d.trades) * 100);
          return `
            <div style="display:flex;justify-content:space-between;gap:8px;padding:4px 0;font-size:.8rem;border-bottom:1px solid var(--bg-tertiary)">
              <span>${d.day}</span>
              <span style="color:var(--text-tertiary);flex:1;text-align:right">${d.trades} trade${d.trades === 1 ? '' : 's'}</span>
              <span class="font-mono" style="font-weight:700">${wr}%</span>
            </div>`;
        }).join('')}
      </div>` : ''}
    </div>`;
}

// Renders + wires the Trade Journal table (Trades sub-view), applying
// `tradeJournalFilter` (module-level state, survives filter changes without
// needing a full renderProgress() re-fetch). Row click opens the read-only
// Trade Detail Sheet; the edit icon jumps straight to the edit form —
// matches the previous card list's exact behavior, just in table form.
function renderTradeJournalTable(el, trades) {
  const todayStr  = new Date().toISOString().split('T')[0];
  const monthStr  = todayStr.slice(0, 7);

  const filtered = trades.filter(t => {
    const isOpen = t.status === 'open';
    if (tradeJournalFilter.status === 'open'   && !isOpen) return false;
    if (tradeJournalFilter.status === 'closed' && isOpen)  return false;
    if (tradeJournalFilter.period === 'month') {
      const relevantDate = t.closedAt ?? t.openedAt;
      if (!relevantDate.startsWith(monthStr)) return false;
    }
    return true;
  }).sort((a, b) => (b.closedAt ?? b.openedAt).localeCompare(a.closedAt ?? a.openedAt));

  el.querySelector('#trade-journal-count').textContent = `${filtered.length} of ${trades.length}`;

  const tableEl = el.querySelector('#trade-journal-table');
  if (filtered.length === 0) {
    tableEl.innerHTML = `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No trades match this filter.</p>`;
    return;
  }

  tableEl.innerHTML = `
    <div style="display:flex;flex-direction:column">
      <div style="display:grid;grid-template-columns:1fr 1.4fr 1fr 0.8fr;gap:8px;padding:6px 4px;font-size:.65rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;border-bottom:1px solid var(--bg-tertiary)">
        <span>Date</span><span>Symbol</span><span style="text-align:right">P&L</span><span style="text-align:right">Status</span>
      </div>
      ${filtered.map(t => {
        const isClosed  = t.status === 'closed' || t.status === 'expired_worthless';
        const pnlAmt    = isClosed ? (t.pnl?.netPnl ?? t.pnl?.grossPnl ?? 0) : null;
        const pnlColor  = pnlAmt != null ? (pnlAmt >= 0 ? 'var(--positive)' : 'var(--negative)') : 'var(--text-secondary)';
        const typeLabel = t.tradeType === 'option' ? `${t.optionType?.toUpperCase() ?? ''} $${t.strikePrice ?? ''}` : 'Stock';
        const statusLabel = t.status === 'open' ? 'OPEN' : t.status === 'expired_worthless' ? 'EXPIRED' : 'CLOSED';
        return `
          <div class="trade-row" data-key="${t.id}" style="display:grid;grid-template-columns:1fr 1.4fr 1fr 0.8fr;gap:8px;padding:10px 4px;align-items:center;cursor:pointer;border-bottom:1px solid var(--bg-tertiary)">
            <span style="font-size:.75rem;color:var(--text-secondary)">${esc(t.closedAt ?? t.openedAt)}</span>
            <span>
              <span style="font-weight:700;font-size:.85rem">${esc(t.ticker)}</span>
              <span style="font-size:.7rem;color:var(--text-tertiary)"> · ${typeLabel}</span>
            </span>
            <span class="font-mono" style="text-align:right;font-size:.8rem;font-weight:700;color:${pnlColor}">${pnlAmt != null ? (pnlAmt >= 0 ? '+' : '') + fmt$(pnlAmt) : '—'}</span>
            <span style="text-align:right;display:flex;justify-content:flex-end;align-items:center;gap:6px">
              <span style="font-size:.65rem;font-weight:600;color:${t.status === 'open' ? 'var(--accent)' : 'var(--text-tertiary)'}">${statusLabel}</span>
              <button class="trade-edit-btn" data-key="${t.id}" style="background:transparent;border:none;cursor:pointer;color:var(--accent);font-size:.75rem;padding:2px">✏️</button>
            </span>
          </div>`;
      }).join('')}
    </div>`;

  tableEl.querySelectorAll('.trade-edit-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const trade = trades.find(t => t.id === btn.dataset.key);
      if (trade && window.openTradeSheet) window.openTradeSheet(trade);
    });
  });
  tableEl.querySelectorAll('.trade-row').forEach(row => {
    row.addEventListener('click', () => {
      const trade = trades.find(t => t.id === row.dataset.key);
      if (trade && window.openTradeDetailSheet) window.openTradeDetailSheet(trade);
    });
  });
}

function skeleton() {
  return `
    <div style="max-width:720px;margin:0 auto">
      <div class="skeleton" style="height:40px;border-radius:10px;margin-bottom:16px"></div>
      <div class="skeleton" style="height:200px;border-radius:16px;margin-bottom:16px"></div>
      <div class="skeleton" style="height:160px;border-radius:16px"></div>
    </div>`;
}
